<template>
  <div class="mod-page list-full">
    <!-- Header -->
    <div class="mod-header">
      <div>
        <h1 class="mod-title">Socios de Negocio</h1>
        <p class="mod-subtitle">Catálogo de clientes, proveedores y contactos</p>
      </div>
      <div class="mod-header-actions">
        <button class="mod-btn mod-btn-outline" @click="exportExcel">
          <i class="bi bi-file-earmark-excel"></i>
          <span>Exportar</span>
        </button>
        <button class="mod-btn mod-btn-outline" @click="sincronizarErp" :disabled="syncing">
          <span v-if="syncing" class="mod-spinner"></span>
          <i v-else class="bi bi-arrow-repeat"></i>
          <span>{{ syncing ? 'Sincronizando...' : 'Sincronizar ERP' }}</span>
        </button>
        <button class="mod-btn mod-btn-primary" @click="openForm()">
          <i class="bi bi-plus-lg"></i>
          <span>Nuevo socio</span>
        </button>
      </div>
    </div>

    <!-- Chips de tipo -->
    <div class="list-chips-bar">
      <button
        class="list-chip"
        :class="{ 'list-chip-on': filterTipo === '' }"
        @click="setTipoFilter('')"
      >
        Todos
        <span class="list-chip-cnt">{{ items.length }}</span>
      </button>
      <button
        v-for="t in tiposConItems"
        :key="t.clave"
        class="list-chip"
        :class="['list-chip-' + t.clave, { 'list-chip-on': filterTipo === t.clave }]"
        @click="setTipoFilter(t.clave)"
      >
        {{ t.nombre }}
        <span class="list-chip-cnt">{{ t.count }}</span>
      </button>
    </div>

    <!-- Controles de búsqueda y filtros -->
    <div class="list-controls">
      <div class="mod-search list-search">
        <i class="bi bi-search"></i>
        <input
          type="text"
          v-model="searchText"
          placeholder="Buscar por clave, nombre, RFC, email..."
          @input="applyFilters"
        >
        <button v-if="searchText" class="list-clear-btn" @click="searchText = ''; applyFilters()">
          <i class="bi bi-x"></i>
        </button>
      </div>
      <select v-model="filterActivo" @change="applyFilters" class="mod-select">
        <option value="">Activo: Todos</option>
        <option value="1">Solo activos</option>
        <option value="0">Solo inactivos</option>
      </select>
      <select v-model="pageSize" @change="applyFilters" class="mod-select">
        <option :value="10">10 por página</option>
        <option :value="25">25 por página</option>
        <option :value="50">50 por página</option>
        <option :value="100">100 por página</option>
      </select>
      <span class="mod-filter-count">{{ totalFiltered }} registro{{ totalFiltered !== 1 ? 's' : '' }}</span>
    </div>

    <!-- Tabla -->
    <div class="mod-table-wrap">
      <table class="mod-table">
        <thead>
          <tr>
            <th class="mod-th-sort" @click="sortBy('Clave')">
              Clave <i :class="sortIcon('Clave')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('NombreComercial')">
              Nombre Comercial <i :class="sortIcon('NombreComercial')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('RFC')">
              RFC <i :class="sortIcon('RFC')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('TipoSocio')">
              Tipo <i :class="sortIcon('TipoSocio')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('ClaveErp')">
              Clave ERP <i :class="sortIcon('ClaveErp')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('EsActivo')">
              Estado <i :class="sortIcon('EsActivo')"></i>
            </th>
            <th class="mod-th-actions">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="7" class="mod-td-center">
              <div class="mod-loading"><span class="mod-spinner"></span> Cargando...</div>
            </td>
          </tr>
          <tr v-else-if="pagedItems.length === 0">
            <td colspan="7" class="mod-td-center">
              <div class="mod-td-empty">
                <i class="bi bi-inbox"></i>
                <span>{{ hayFiltros ? 'Sin resultados para los filtros aplicados' : 'No hay socios de negocio registrados' }}</span>
                <button v-if="hayFiltros" class="list-clear-filters" @click="clearFilters">
                  Limpiar filtros
                </button>
              </div>
            </td>
          </tr>
          <tr v-for="item in pagedItems" :key="item.Clave + '-' + item.EmpresaId" class="mod-tr">
            <td><span class="mod-badge">{{ item.Clave }}</span></td>
            <td>
              <div class="list-nombre-main">{{ item.NombreComercial }}</div>
              <div v-if="item.RazonSocial && item.RazonSocial !== item.NombreComercial" class="list-nombre-sub">
                {{ item.RazonSocial }}
              </div>
            </td>
            <td class="mod-td-muted">{{ item.RFC || '—' }}</td>
            <td>
              <span class="list-tipo-badge" :class="'list-tipo-' + (item.TipoSocio || '').toLowerCase()">
                {{ tipoNombre(item.TipoSocio) }}
              </span>
            </td>
            <td class="mod-td-muted">{{ item.ClaveErp || '—' }}</td>
            <td>
              <span :class="item.EsActivo ? 'mod-status-active' : 'mod-status-inactive'">
                {{ item.EsActivo ? 'Activo' : 'Inactivo' }}
              </span>
            </td>
            <td class="mod-td-actions">
              <button
                class="mod-btn-icon"
                title="Crear copia como otro tipo"
                @click="openConvert(item)"
              >
                <i class="bi bi-arrow-left-right"></i>
              </button>
              <button class="mod-btn-icon" title="Editar" @click="openForm(item)">
                <i class="bi bi-pencil"></i>
              </button>
              <button class="mod-btn-icon mod-btn-danger" title="Eliminar" @click="confirmDelete(item)">
                <i class="bi bi-trash3"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Paginacion -->
    <div class="mod-pagination" v-if="totalPages > 1">
      <button class="mod-page-btn" :disabled="currentPage === 1" @click="currentPage = 1">
        <i class="bi bi-chevron-double-left"></i>
      </button>
      <button class="mod-page-btn" :disabled="currentPage === 1" @click="currentPage--">
        <i class="bi bi-chevron-left"></i>
      </button>
      <template v-for="p in visiblePages" :key="p">
        <button
          class="mod-page-btn"
          :class="{ 'mod-page-active': p === currentPage }"
          @click="currentPage = p"
        >{{ p }}</button>
      </template>
      <button class="mod-page-btn" :disabled="currentPage === totalPages" @click="currentPage++">
        <i class="bi bi-chevron-right"></i>
      </button>
      <button class="mod-page-btn" :disabled="currentPage === totalPages" @click="currentPage = totalPages">
        <i class="bi bi-chevron-double-right"></i>
      </button>
      <span class="mod-page-info">Página {{ currentPage }} de {{ totalPages }}</span>
    </div>

    <!-- Modal Form -->
    <SocioNegocioForm
      v-if="showForm"
      :item="selectedItem"
      :is-edit="isEdit"
      :is-conversion="isConversion"
      @saved="onSaved"
      @close="showForm = false"
    />
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import socionegocioService from './socionegocio.service';
import authService from '@/services/auth';
import { showSyncToast, showSyncError } from '@/shared/utils/toast';
import SocioNegocioForm from './SocioNegocioForm.vue';

const TIPO_LABELS_PLURAL = { C: 'Clientes', P: 'Proveedores', X: 'Prospectos', E: 'Empleados', A: 'Alumnos' };
const TIPO_LABELS        = { C: 'Cliente',  P: 'Proveedor',  X: 'Prospecto',  E: 'Empleado',  A: 'Alumno'  };
const TIPO_ORDER = ['C', 'P', 'X', 'E', 'A'];

export default {
  name: 'SocioNegocioList',
  components: { SocioNegocioForm },
  data() {
    return {
      items: [],
      filteredItems: [],
      searchText: '',
      filterTipo: '',
      filterActivo: '',
      loading: false,
      syncing: false,
      showForm: false,
      selectedItem: null,
      isEdit: false,
      isConversion: false,
      currentPage: 1,
      pageSize: 25,
      sortField: 'Clave',
      sortAsc: true
    };
  },
  computed: {
    hayFiltros() {
      return !!(this.searchText || this.filterTipo || this.filterActivo);
    },
    totalFiltered() {
      return this.filteredItems.length;
    },
    totalPages() {
      return Math.ceil(this.totalFiltered / this.pageSize) || 1;
    },
    pagedItems() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredItems.slice(start, start + this.pageSize);
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      let start = Math.max(1, current - 2);
      let end = Math.min(total, current + 2);
      if (end - start < 4) {
        if (start === 1) end = Math.min(total, start + 4);
        else start = Math.max(1, end - 4);
      }
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
    tiposConItems() {
      const counts = {};
      for (const item of this.items) {
        const t = (item.TipoSocio || '').toUpperCase();
        if (t) counts[t] = (counts[t] || 0) + 1;
      }
      return TIPO_ORDER
        .filter(k => counts[k] > 0)
        .map(k => ({ clave: k, nombre: TIPO_LABELS_PLURAL[k] || k, count: counts[k] }));
    }
  },
  watch: {
    currentPage(val) {
      if (val > this.totalPages) this.currentPage = this.totalPages;
    }
  },
  created() {
    this.loadItems();
  },
  methods: {
    async loadItems() {
      this.loading = true;
      try {
        const empresa = authService.getSelectedEmpresa();
        const result = await socionegocioService.listar({ EmpresaId: empresa?.EmpresaId || 0 });
        if (Array.isArray(result)) {
          this.items = result;
        } else if (result.OData) {
          this.items = result.OData;
        } else {
          this.items = [];
        }
        this.applyFilters();
      } catch (e) {
        console.error('Error al cargar socios de negocio:', e);
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      let data = [...this.items];

      if (this.filterTipo) {
        data = data.filter(i => (i.TipoSocio || '').toUpperCase() === this.filterTipo);
      }

      if (this.filterActivo !== '') {
        const activo = this.filterActivo === '1';
        data = data.filter(i => !!i.EsActivo === activo);
      }

      const q = this.searchText.toLowerCase().trim();
      if (q) {
        data = data.filter(i =>
          (i.Clave || '').toLowerCase().includes(q) ||
          (i.NombreComercial || '').toLowerCase().includes(q) ||
          (i.RazonSocial || '').toLowerCase().includes(q) ||
          (i.RFC || '').toLowerCase().includes(q) ||
          (i.Email || '').toLowerCase().includes(q) ||
          (i.ClaveErp || '').toLowerCase().includes(q)
        );
      }

      data.sort((a, b) => {
        let va = a[this.sortField];
        let vb = b[this.sortField];
        if (va == null) va = '';
        if (vb == null) vb = '';
        if (typeof va === 'string') va = va.toLowerCase();
        if (typeof vb === 'string') vb = vb.toLowerCase();
        if (va < vb) return this.sortAsc ? -1 : 1;
        if (va > vb) return this.sortAsc ? 1 : -1;
        return 0;
      });

      this.filteredItems = data;
      this.currentPage = 1;
    },
    setTipoFilter(tipo) {
      this.filterTipo = tipo;
      this.applyFilters();
    },
    clearFilters() {
      this.searchText = '';
      this.filterTipo = '';
      this.filterActivo = '';
      this.applyFilters();
    },
    sortBy(field) {
      if (this.sortField === field) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortField = field;
        this.sortAsc = true;
      }
      this.applyFilters();
    },
    sortIcon(field) {
      if (this.sortField !== field) return 'bi bi-chevron-expand mod-sort-inactive';
      return this.sortAsc ? 'bi bi-chevron-up' : 'bi bi-chevron-down';
    },
    tipoNombre(tipo) {
      return TIPO_LABELS[String(tipo || '').toUpperCase()] || tipo || '—';
    },
    openForm(item = null) {
      this.isEdit = !!item;
      this.isConversion = false;
      this.selectedItem = item ? { ...item } : null;
      this.showForm = true;
    },
    openConvert(item) {
      this.isEdit = false;
      this.isConversion = true;
      this.selectedItem = item ? { ...item } : null;
      this.showForm = true;
    },
    onSaved() {
      this.showForm = false;
      this.loadItems();
    },
    async confirmDelete(item) {
      if (!confirm(`Eliminar socio de negocio "${item.NombreComercial}"?`)) return;
      try {
        const result = await socionegocioService.borrar(item);
        if (result.Number >= 0) {
          this.loadItems();
        } else {
          alert(result.Message || 'Error al eliminar');
        }
      } catch (e) {
        console.error('Error al eliminar:', e);
        alert('Error al eliminar');
      }
    },
    async sincronizarErp() {
      this.syncing = true;
      try {
        const result = await socionegocioService.sincronizarErp();
        showSyncToast(result);
        if (result.Number >= 0) {
          await this.loadItems();
        }
      } catch (e) {
        console.error('Error al sincronizar:', e);
        showSyncError('Error de conexión al sincronizar');
      } finally {
        this.syncing = false;
      }
    },
    exportExcel() {
      const data = this.filteredItems.map(i => ({
        Clave: i.Clave,
        'Nombre Comercial': i.NombreComercial || '',
        'Razon Social': i.RazonSocial || '',
        RFC: i.RFC || '',
        Tipo: this.tipoNombre(i.TipoSocio),
        'Clave ERP': i.ClaveErp || '',
        Activo: i.EsActivo ? 'Si' : 'No'
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const colWidths = Object.keys(data[0] || {}).map(k => ({
        wch: Math.max(k.length, ...data.map(r => String(r[k] || '').length)) + 2
      }));
      ws['!cols'] = colWidths;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'SociosNegocio');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'socios_negocio.xlsx');
    }
  }
};
</script>

<style scoped>
@import "@/modules/_shared/catalog.css";

/* ── Ancho completo ─────────────────────────────────── */
.mod-page.list-full {
  max-width: none;
  padding: 1.5rem 2rem;
}

/* ── Chips de tipo ──────────────────────────────────── */
.list-chips-bar {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
}

.list-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.8rem;
  border-radius: 20px;
  border: 1.5px solid #e2e8f0;
  background: #f7fafc;
  color: #4a5568;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.list-chip-cnt {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.4rem;
  padding: 0 0.35rem;
  height: 1.3rem;
  background: rgba(0,0,0,0.08);
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
}

/* Estado activo (siempre azul primario) */
.list-chip-on {
  background: linear-gradient(135deg, var(--t-primary-dark, #0d5ca0), var(--t-primary, #1272c4)) !important;
  color: #fff !important;
  border-color: transparent !important;
}

.list-chip-on .list-chip-cnt {
  background: rgba(255, 255, 255, 0.25) !important;
  color: #fff !important;
}

/* Colores por tipo (estado inactivo) */
.list-chip-C { border-color: #bee3f8; color: #2b6cb0; background: #ebf4ff; }
.list-chip-P { border-color: #9ae6b4; color: #276749; background: #f0fff4; }
.list-chip-X { border-color: #fbd38d; color: #7b341e; background: #fffaf0; }
.list-chip-E { border-color: #d6bcfa; color: #553c9a; background: #faf5ff; }
.list-chip-A { border-color: #81e6d9; color: #234e52; background: #e6fffa; }

.list-chip-C:not(.list-chip-on):hover { background: #bee3f8; }
.list-chip-P:not(.list-chip-on):hover { background: #9ae6b4; color: #22543d; }
.list-chip-X:not(.list-chip-on):hover { background: #fbd38d; }
.list-chip-E:not(.list-chip-on):hover { background: #d6bcfa; }
.list-chip-A:not(.list-chip-on):hover { background: #81e6d9; }

/* ── Barra de controles ─────────────────────────────── */
.list-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.list-search {
  flex: 1;
  min-width: 200px;
  max-width: 480px;
  position: relative;
}

.list-clear-btn {
  background: none;
  border: none;
  padding: 0.15rem 0.3rem;
  color: #a0aec0;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  border-radius: 4px;
  transition: color 0.15s;
}

.list-clear-btn:hover {
  color: #4a5568;
}

/* ── Nombre en tabla ────────────────────────────────── */
.list-nombre-main {
  font-weight: 500;
  color: #1a202c;
  line-height: 1.3;
}

.list-nombre-sub {
  font-size: 0.78rem;
  color: #718096;
  margin-top: 0.2rem;
  line-height: 1.3;
}

/* ── Badge de tipo en tabla ─────────────────────────── */
.list-tipo-badge {
  display: inline-block;
  padding: 0.2rem 0.65rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.list-tipo-c { background: #ebf4ff; color: #2b6cb0; }
.list-tipo-p { background: #f0fff4; color: #276749; }
.list-tipo-x { background: #fffaf0; color: #7b341e; }
.list-tipo-e { background: #faf5ff; color: #553c9a; }
.list-tipo-a { background: #e6fffa; color: #234e52; }

/* ── Vacío con limpiar filtros ──────────────────────── */
.list-clear-filters {
  margin-top: 0.5rem;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.35rem 0.85rem;
  font-size: 0.8rem;
  color: var(--t-primary, #1272c4);
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}

.list-clear-filters:hover {
  background: #ebf4ff;
  border-color: var(--t-primary, #1272c4);
}

/* ── Responsive ─────────────────────────────────────── */
@media (max-width: 768px) {
  .mod-page.list-full {
    padding: 1rem 0.75rem;
  }

  .list-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .list-search {
    max-width: none;
  }
}
</style>
